import type { TourPlacement } from './tour-types';

export type Box = { top: number; left: number; width: number; height: number };

type PlaceBubbleParams = {
  anchor: Box;
  bubble: { width: number; height: number };
  viewport: { width: number; height: number };
  preferred?: TourPlacement;
};

export type BubblePosition = { top: number; left: number; side: TourPlacement };

type SpotlightRingPathParams = { width: number; height: number; radius: number; thickness: number };

/** A rounded rectangle as SVG path commands, drawn clockwise from the top left. */
const roundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  return [
    `M${x + r},${y}`,
    `H${x + width - r}`,
    `A${r},${r} 0 0 1 ${x + width},${y + r}`,
    `V${y + height - r}`,
    `A${r},${r} 0 0 1 ${x + width - r},${y + height}`,
    `H${x + r}`,
    `A${r},${r} 0 0 1 ${x},${y + height - r}`,
    `V${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    'Z',
  ].join(' ');
};

/**
 * The spotlight ring's shape, for `clip-path`: a rounded rectangle with a smaller one
 * cut out of it (even-odd), leaving a hollow band `thickness` wide. The element under it
 * shows through the middle.
 */
export const spotlightRingPath = ({ width, height, radius, thickness }: SpotlightRingPathParams): string =>
  `path(evenodd, "${roundedRect(0, 0, width, height, radius)} ${roundedRect(
    thickness,
    thickness,
    width - thickness * 2,
    height - thickness * 2,
    radius - thickness,
  )}")`;

const GAP = 14;
const EDGE = 8;

const ORDER: Record<TourPlacement, TourPlacement[]> = {
  right: ['right', 'bottom', 'top', 'left'],
  bottom: ['bottom', 'top', 'right', 'left'],
  left: ['left', 'bottom', 'top', 'right'],
  top: ['top', 'bottom', 'right', 'left'],
};

/**
 * Where the bubble goes: the preferred side if it fits, otherwise the next side that
 * does, and always clamped inside the window so it can never be cut off. Viewport
 * coordinates, for a `position: fixed` bubble.
 */
export function placeBubble({ anchor, bubble, viewport, preferred = 'right' }: PlaceBubbleParams): BubblePosition {
  const fits: Record<TourPlacement, boolean> = {
    right: anchor.left + anchor.width + GAP + bubble.width <= viewport.width - EDGE,
    left: anchor.left - GAP - bubble.width >= EDGE,
    bottom: anchor.top + anchor.height + GAP + bubble.height <= viewport.height - EDGE,
    top: anchor.top - GAP - bubble.height >= EDGE,
  };
  const side = ORDER[preferred].find((candidate) => fits[candidate]) ?? 'bottom';

  const raw: Record<TourPlacement, { top: number; left: number }> = {
    right: { top: anchor.top, left: anchor.left + anchor.width + GAP },
    left: { top: anchor.top, left: anchor.left - GAP - bubble.width },
    bottom: { top: anchor.top + anchor.height + GAP, left: anchor.left },
    top: { top: anchor.top - GAP - bubble.height, left: anchor.left },
  };

  const clamp = (value: number, max: number) => Math.max(EDGE, Math.min(value, max - EDGE));
  return {
    side,
    top: clamp(raw[side].top, viewport.height - bubble.height),
    left: clamp(raw[side].left, viewport.width - bubble.width),
  };
}
